import { useTranslation } from 'react-i18next'
import { Box, LinearProgress, Typography } from '@mui/material'
import type { Card } from '@/types'

function getProgressColor(percent: number): 'error' | 'warning' | 'success' {
  if (percent <= 30) return 'error'
  if (percent <= 70) return 'warning'
  return 'success'
}

interface CardProgressProps {
  card: Pick<
    Card,
    | 'translation_progress'
    | 'pronunciation_progress'
    | 'spelling_progress'
    | 'reverse_translation_progress'
    | 'listening_progress'
    | 'progress'
  >
}

const PROGRESS_KEYS = [
  { key: 'translation_progress', labelKey: 'cards.translation_progress' },
  { key: 'pronunciation_progress', labelKey: 'cards.pronunciation_progress' },
  { key: 'spelling_progress', labelKey: 'cards.spelling_progress' },
  { key: 'reverse_translation_progress', labelKey: 'cards.reverse_translation_progress' },
  { key: 'listening_progress', labelKey: 'cards.listening_progress' },
] as const

export function CardProgressDisplay({ card }: CardProgressProps) {
  const { t } = useTranslation()

  const overallPercent = Math.round(card.progress ?? 0)
  const overallColor = getProgressColor(overallPercent)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('cards.overall_progress')} ({overallPercent}%)
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, overallPercent))}
          color={overallColor}
          sx={{ height: 10, borderRadius: 1 }}
        />
      </Box>
      {PROGRESS_KEYS.map(({ key, labelKey }) => {
        const value = card[key] ?? 0
        const percent = Math.round(value)
        return (
          <Box key={key}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t(labelKey)} ({percent}%)
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, percent))}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
        )
      })}
    </Box>
  )
}
